import { useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { NavLink } from './NavLink';
import { GlobalSearchBar } from './GlobalSearchBar';
import { UserMenu } from './UserMenu';
import { NotificationDropdown } from './NotificationDropdown';
import { GetStartedDropdown } from './GetStartedDropdown';

import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { useNavBreakpoints } from '@/hooks/useNavBreakpoints';
import { ROLE_TYPES } from '@scio3/shared-types';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@scio3/ui';

export function TopNavigation() {
  const { t } = useTranslation();
  const navRef = useRef<HTMLElement>(null);
  const collapseLevel = useNavBreakpoints(navRef);
  const company = useAuthStore((s) => s.company);
  const permissionsLoaded = useAuthStore((s) => s.me != null);
  const { isRootAdmin, teamRole } = usePermissions();
  const isWhiteLabel = !!company?.brandingData?.isWhiteLabel;

  // Wait for permissions to load before showing restricted nav items.
  // While me is loading, teamRole is undefined → hide restricted items to prevent
  // flash of forbidden links for read-only users (they appear then disappear).
  const isRestrictedRole =
    teamRole === ROLE_TYPES.READ_ONLY ||
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL ||
    teamRole === ROLE_TYPES.CONTENT_APPROVAL;

  const useNativeEngage = import.meta.env.VITE_USE_NATIVE_ENGAGE === 'true';
  const useNativeOptiDev = import.meta.env.VITE_USE_NATIVE_OPTIDEV === 'true';
  // Engage visible for all roles EXCEPT CONTENT_PROPOSAL & CONTENT_APPROVAL
  // (READ_ONLY users can browse but not edit)
  const isEngageRestrictedRole =
    teamRole === ROLE_TYPES.CONTENT_PROPOSAL || teamRole === ROLE_TYPES.CONTENT_APPROVAL;
  const showEngage =
    permissionsLoaded && !isEngageRestrictedRole && (company?.feature?.engage || useNativeEngage);
  const showTemplates = permissionsLoaded && !isRestrictedRole;
  const showOptiDev = permissionsLoaded && !isRestrictedRole && useNativeOptiDev;
  // scio1 requires principal.isRootAdmin() (Owner/Admin/Super Admin only)
  const showAnalytics = permissionsLoaded && isRootAdmin && !!company?.feature?.proofOfPlay;

  // Collapse levels:
  // 1: search → icon-only
  // 2: logo → icon-only
  // 3: user menu → avatar-only
  // 4: help + notification hidden
  // 5: engage + analytics → "More" dropdown
  // 6: optidev → "More" dropdown
  const compactSearch = collapseLevel >= 1;
  const compactLogo = collapseLevel >= 2;
  const compactUser = collapseLevel >= 3;
  const hideHelpNotifications = collapseLevel >= 4;
  const analyticsInMore = collapseLevel >= 5;
  const engageInMore = collapseLevel >= 5;
  const optidevInMore = collapseLevel >= 6;

  // Track which optional items overflow into "More"
  const hasMoreItems =
    (showAnalytics && analyticsInMore) ||
    (showEngage && engageInMore) ||
    (showOptiDev && optidevInMore);

  return (
    <nav ref={navRef} className="sticky top-0 z-50 border-b border-border bg-card">
      <div className="flex h-14 items-center justify-between px-4">
        {/* Left: Logo + Nav Items */}
        <div className="flex items-center gap-0.5">
          <Link to="/app/screenManagement" className="mr-5 flex items-center">
            {company?.brandingData?.logo ? (
              <img
                src={company.brandingData.logo as string}
                alt="Logo"
                className={compactLogo ? 'h-7 w-7 object-contain' : 'h-7'}
              />
            ) : compactLogo ? (
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-sm font-bold text-primary-foreground">
                O
              </span>
            ) : (
              <span className="text-lg font-bold text-primary">{t('OptiSigns')}</span>
            )}
          </Link>

          {/* Core nav — always visible */}
          <NavLink to="/app/screenManagement">{t('Screens')}</NavLink>
          <NavLink to="/app/assetManagement">{t('Files/Assets')}</NavLink>
          <NavLink to="/app/playlistManagement">{t('Playlists')}</NavLink>
          <NavLink to="/app/scheduleManagement">{t('Schedules')}</NavLink>
          {showTemplates && <NavLink to="/app/templateManagement">{t('Templates')}</NavLink>}

          {/* Conditionally visible nav items — order: Engage, OptiDev, Analytics */}
          {showEngage && !engageInMore && (
            <NavLink to="/app/engageManagement">{t('Engage')}</NavLink>
          )}
          {showOptiDev && !optidevInMore && <NavLink to="/app/optidev">{t('OptiDev')}</NavLink>}
          {showAnalytics && !analyticsInMore && <AnalyticsDropdown isWhiteLabel={isWhiteLabel} />}

          {/* "More" dropdown for overflowed items */}
          {hasMoreItems && (
            <MoreDropdown
              showAnalytics={showAnalytics && analyticsInMore}
              showEngage={showEngage && engageInMore}
              showOptiDev={showOptiDev && optidevInMore}
              isWhiteLabel={isWhiteLabel}
            />
          )}

          <GlobalSearchBar compact={compactSearch} />
        </div>

        {/* Right: Get Started + Notifications + User Menu */}
        <div className="flex items-center gap-2">
          {!isWhiteLabel && !hideHelpNotifications && <GetStartedDropdown />}
          {!hideHelpNotifications && <NotificationDropdown />}
          <UserMenu compact={compactUser} />
        </div>
      </div>
    </nav>
  );
}

const ANALYTICS_ROUTES = ['/app/playbackReport', '/app/ai-analytics'];

function AnalyticsDropdown({ isWhiteLabel }: { isWhiteLabel: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const isActive = ANALYTICS_ROUTES.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'inline-flex h-14 items-center gap-1 rounded-none border-b-2 px-3 text-sm font-medium transition-colors hover:bg-transparent',
            isActive
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
          )}
        >
          {t('Analytics')} <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuItem asChild>
          <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
        </DropdownMenuItem>
        {!isWhiteLabel && (
          <DropdownMenuItem asChild>
            <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MoreDropdownProps {
  showAnalytics: boolean;
  showEngage: boolean;
  showOptiDev: boolean;
  isWhiteLabel: boolean;
}

function MoreDropdown({ showAnalytics, showEngage, showOptiDev, isWhiteLabel }: MoreDropdownProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const moreRoutes = [
    ...(showEngage ? ['/app/engageManagement'] : []),
    ...(showAnalytics ? ANALYTICS_ROUTES : []),
    ...(showOptiDev ? ['/app/optidev'] : []),
  ];
  const isActive = moreRoutes.some((route) => pathname.startsWith(route));

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            'inline-flex h-14 items-center gap-1 rounded-none border-b-2 px-3 text-sm font-medium transition-colors hover:bg-transparent',
            isActive
              ? 'border-primary text-foreground'
              : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
          )}
        >
          <span>{t('More')}</span>
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {showEngage && (
          <DropdownMenuItem asChild>
            <Link to="/app/engageManagement">{t('Engage')}</Link>
          </DropdownMenuItem>
        )}
        {showOptiDev && (
          <>
            {showEngage && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to="/app/optidev">{t('OptiDev')}</Link>
            </DropdownMenuItem>
          </>
        )}
        {showAnalytics && (
          <>
            {(showEngage || showOptiDev) && <DropdownMenuSeparator />}
            <DropdownMenuItem asChild>
              <Link to="/app/playbackReport">{t('Proof of Play')}</Link>
            </DropdownMenuItem>
            {!isWhiteLabel && (
              <DropdownMenuItem asChild>
                <Link to="/app/ai-analytics">{t('AI Analytics')}</Link>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
